import React, { useState, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import Button from '../../../../../../../../components/global/Button';
import { getAsset } from '../../../../../../../../util/assets';
import CardElementPhotoSelectionControl from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import PrintPackageConfigurableItemsListModal from '../../PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageConfigurableItemsListModal';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import PrintProductGroupConfiguratorModal from '../../../../../../../../services/prints/components/PrintProductGroupConfigurator/modal';
import { getPhotoOrientation } from '../../PurchasingOptions/components/PrintProductsGroup/helpers';
import { PrintProductConfiguratorModal } from '../../../../../../../../services/prints/components/PrintProductConfigurator';
import PrintPackageConfigurableItemCompleteModal from '../../PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageConfigurableItemCompleteModal';
import {
  getPrintPackagePrintBackgrounds,
  printPackageRequiresConfiguration,
} from '../../../../../../../../util/print-packages';
import useAlbumPrintPackageCartConfigurator from '../../PurchasingOptions/components/AlbumPrintPackageOption/useAlbumPrintPackageCartConfigurator';
import { getPrintSizeServiceIds } from '../../../../../../../../util/print-product-configuration';
import { isStandardPrint } from '../../../../../../../../util/print-product';
import PrintProductSizeRoesConfiguratorModal from '../../PurchasingOptions/components/PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../../../../../constants/pro-custom-prints';
import PrintPackageDigitalDownloadAccountCardModal from '../../PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageDigitalDownloadAccountCardModal/PrintPackageDigitalDownloadAccountCardModal';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import useIdentityJerseyColors from '../../../hooks/useIdentityJerseyColors';
import PhotoSelectionOverlayModal from '../../PurchasingOptions/components/DigitalBundleOption/components/PhotoSelectionOverlayModal';

const propTypes = {
  className: PropTypes.string,
  albumMembershipPhoto: PropTypes.shape({
    photo: PropTypes.shape({
      id: PropTypes.string,
      foreground: PropTypes.shape({
        original_url: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }),
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  albumPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_package: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  purchasedPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_package: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
    album: PropTypes.shape({
      root_album_folder: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }),
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onUpdate: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  onConfigure: PropTypes.func,
  onConfigurationComplete: PropTypes.func,
  onPurchasedPrintPackageToggle: PropTypes.func.isRequired,
  isAllPhotos: PropTypes.bool,
  isGreenScreenEnabled: PropTypes.bool,
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
};

const AlbumPrintPackageOption = ({
  className,
  albumMembershipPhoto,
  albumPrintPackage,
  purchasedPrintPackage,
  albumMembershipGroup,
  cartItem,
  onUpdate,
  onCheckout,
  onConfigure,
  onConfigurationComplete,
  onPurchasedPrintPackageToggle,
  isAllPhotos = false,
  isGreenScreenEnabled = false,
  identity,
  watermarkSettings,
}) => {
  const [selectingPhoto, setSelectingPhoto] = useState(false);
  const [
    digitalDownloadAccountCardModalOpen,
    setDigitalDownloadAccountCardModalOpen,
  ] = useState(false);

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const allowMultiPose =
    isMultiPosePackageMembership(albumMembershipGroup) ?? false;

  const classes = classNames({
    [className]: !!className,
    'purchases-pending-configuration-option-item': true,
    'purchases-pending-configuration-option-item-album-print-package': true,
    'is-selected': !!cartItem,
  });

  const { print_package: printPackage } = albumPrintPackage;
  const { photo } = cartItem ?? {};

  const [configurableItemsListModalOpen, setConfigurableItemsListModalOpen] =
    useState(false);
  const [configurableItemsListModalProps, setConfigurableItemsListModalProps] =
    useState(null);

  const {
    activeConfigurableItem,
    activeConfig,
    onConfigurationStart,
    onConfigurationCancel,
    configurableItems,
    onItemConfigured,
    onItemsConfigured,
    onConfigurableItemUpdate,
  } = useAlbumPrintPackageCartConfigurator({
    albumPrintPackage,
    albumPrintPackageCartItem: cartItem,
    onItemConfiguredComplete: ({
      completeConfigurableItem,
      completeConfigurableItems,
      remainingConfigurableItems,
    }) => {
      if (remainingConfigurableItems.length === 0 && onConfigurationComplete) {
        onConfigurationComplete({ purchasedPrintPackage });
      } else {
        setConfigurableItemsListModalProps({
          printPackage,
          completeConfigurableItem,
          remainingConfigurableItems,
          completeConfigurableItems,
        });
      }
    },
    onUpdateCartItem: onUpdate,
    allowMultiPose,
  });

  const remainingItems = configurableItems?.length ?? 0;

  const configurablePrintProductItems = useMemo(
    () =>
      configurableItems
        .filter(
          ({ print_product_configuration: printProductConfig = {} }) =>
            !!printProductConfig?.print_product?.card,
        )
        .sort((a, b) => {
          if (isStandardPrint(a?.print_product_configuration?.print_product)) {
            return -1;
          }
          if (isStandardPrint(b?.print_product_configuration?.print_product)) {
            return 1;
          }

          if (!b?.print_product_configuration?.print_product) {
            return -1;
          }

          if (!a?.print_product_configuration?.print_product) {
            return -1;
          }

          return (
            a?.print_product_configuration?.print_product?.name ?? ''
          ).localeCompare(
            b?.print_product_configuration?.print_product?.name ?? '',
          );
        }),
    [configurableItems],
  );
  const requiresConfiguration = printPackageRequiresConfiguration(
    printPackage,
    allowMultiPose,
  );

  const printBackgrounds = isGreenScreenEnabled
    ? getPrintPackagePrintBackgrounds(
        albumPrintPackage,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];

  const prepaidThumbClasses = classNames({
    'purchases-pending-configuration-option-item-thumb': true,
    'thumb-image-fill': !!printPackage?.preview_asset_image?.url,
  });

  return (
    <div className={classes}>
      <div className={prepaidThumbClasses}>
        <img
          src={
            printPackage?.preview_asset_image?.url ??
            getAsset('/icon-package-3d.svg')
          }
          alt=""
        />
      </div>

      <div className="purchases-pending-configuration-option-item-content-wrap">
        <span className="purchases-pending-configuration-option-item-title">
          {printPackage.name}
        </span>
        {remainingItems > 0 && cartItem && (
          <Button
            pill
            btnOutline
            btnSize="sm"
            className="text-bold"
            onClick={onConfigurationStart}
          >
            Configure ({remainingItems}) more{' '}
            {pluralize('item', remainingItems)}
          </Button>
        )}
        {(remainingItems === 0 || !cartItem) && (
          <Button
            pill
            btnSize="sm"
            btnOutline={!cartItem}
            disabled={!requiresConfiguration && !!cartItem}
            className="text-bold"
            onClick={() => {
              onConfigure?.();
              if (!cartItem && albumMembershipPhoto) {
                if (requiresConfiguration) {
                  onConfigurationStart();
                } else if (onConfigurationComplete) {
                  onConfigurationComplete({ purchasedPrintPackage });
                }
                onPurchasedPrintPackageToggle({
                  ...albumMembershipPhoto,
                  printBackgrounds,
                });
                return;
              }

              if (!cartItem) {
                setSelectingPhoto(true);
                return;
              }

              if (cartItem && requiresConfiguration) {
                setConfigurableItemsListModalOpen(true);
              }
            }}
          >
            {requiresConfiguration ? (
              <Fragment>
                {cartItem ? 'Update Configurations' : 'Configure now'}
              </Fragment>
            ) : (
              <Fragment>{cartItem ? 'Added to cart!' : 'Select'}</Fragment>
            )}
          </Button>
        )}
      </div>
      <CardElementPhotoSelectionControl
        forceOpen={selectingPhoto}
        onClose={() => setSelectingPhoto(false)}
        onRecordChange={matchedPhoto => {
          if (requiresConfiguration) {
            onConfigurationStart();
          } else if (onConfigurationComplete) {
            onConfigurationComplete({ purchasedPrintPackage });
          }

          if (matchedPhoto?.__typename === 'Photo') {
            onPurchasedPrintPackageToggle({
              photo: matchedPhoto,
              album:
                matchedPhoto.albums?.records?.[0] ??
                albumMembershipGroup?.album,
              printBackgrounds: matchedPhoto?.foreground?.original_url
                ? getPrintPackagePrintBackgrounds(
                    albumPrintPackage,
                    albumMembershipGroup,
                    jerseyColors,
                  )
                : [],
            });
          } else {
            onPurchasedPrintPackageToggle({
              ...matchedPhoto,
              printBackgrounds: matchedPhoto?.photo?.foreground?.original_url
                ? getPrintPackagePrintBackgrounds(
                    albumPrintPackage,
                    albumMembershipGroup,
                    jerseyColors,
                  )
                : [],
            });
          }
        }}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
          identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
        }}
        mappings={
          isAllPhotos
            ? undefined
            : [
                {
                  id: 'allPhotos',
                  label: 'All Photos',
                  path: 'all_photos',
                },
                {
                  id: 'matches',
                  label: 'Matches',
                  path: allowMultiIdentityPhoto
                    ? 'matched_photos_extended'
                    : 'identities[0].matched_photos_extended',
                },
              ]
        }
        uploadDisabled
        watermarkSettings={watermarkSettings}
      />
      <PhotoSelectionOverlayModal
        forceOpen={activeConfigurableItem?.digital_bundle}
        onClose={onConfigurationCancel}
        maxPhotos={activeConfigurableItem?.digital_bundle?.photos_count}
        albumMembershipGroupId={
          isAllPhotos ? undefined : albumMembershipGroup?.id
        }
        albumId={isAllPhotos ? albumMembershipGroup?.id : undefined}
        currentSelectedPhotos={photo ? [photo] : []}
        identityId={allowMultiIdentityPhoto ? undefined : identity?.id}
        onComplete={({ photos }) => onItemConfigured({ photos })}
        watermarkSettings={watermarkSettings}
      />
      {configurableItemsListModalOpen && (
        <PrintPackageConfigurableItemsListModal
          forceOpen
          cartItem={cartItem}
          onConfigurableItemUpdate={configurableItem =>
            onConfigurableItemUpdate(configurableItem)
          }
          onClose={() => setConfigurableItemsListModalOpen(false)}
        />
      )}
      {cartItem && (
        // Do not rely on checking for the activeConfigurationItem to be
        // available since it may not have the print_product_size config
        // at the time. Instead, just rely on the photo alone, and go w/
        // usePrintProductConfiguration.js' handlePrintProductSizeSelect
        // which will automatically reset to the available orientation if
        // the requested one is not available.
        <>
          <AccountCardAlbumCustomPrintConfiguratorModal
            forceOpen={
              !!activeConfigurableItem?.custom_print &&
              activeConfigurableItem?.custom_print?.subtype !==
                CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
            }
            albumCustomPrint={{
              custom_print: activeConfigurableItem?.custom_print,
            }}
            onDismiss={onConfigurationCancel}
            onFinished={({ accountCard }) => {
              onItemConfigured({ accountCard });
            }}
            buttonText="Continue"
            albumMembershipGroup={albumMembershipGroup}
            deleteAccountCardOnDismiss={!activeConfig}
            defaultAccountCard={activeConfig?.accountCard}
            isAllPhotos={isAllPhotos}
            photo={photo}
            watermarkSettings={watermarkSettings}
          />
          <CardElementPhotoSelectionControl
            uploadDisabled
            title={`Select ${pluralize(
              'photo',
              activeConfigurableItem?.custom_print?.photos_no,
              true,
            )} for: ${activeConfigurableItem?.custom_print?.name}`}
            photosContext={{
              album_id:
                albumMembershipGroup &&
                (albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              album_folder_id:
                albumMembershipGroup &&
                (!albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              root_album_folder_id:
                albumMembershipGroup && albumMembershipGroup.is_album
                  ? albumMembershipGroup.album.root_album_folder?.id
                  : undefined,
              // We rely on a resolver that the membershipGroup would have by
              // default when we don't create a pseudo membership (identities).
              // This allows us to actually discern if this is a membership or not.
              is_album_membership_group: !!albumMembershipGroup?.identities,
              is_all_photos: isAllPhotos,
              identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
            }}
            forceOpen={
              !!activeConfigurableItem?.custom_print &&
              activeConfigurableItem?.custom_print?.subtype ===
                CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
            }
            onClose={onConfigurationCancel}
            maxPhotosCount={activeConfigurableItem?.custom_print?.photos_no}
            currentSelectedPhotos={
              activeConfig?.photos
                ? activeConfig.photos.map(
                    ({ photo: selectedPhoto }) => selectedPhoto,
                  )
                : [photo]
            }
            currentSelectedPhoto={
              allowMultiPose &&
              activeConfigurableItem?.custom_print?.photos_no === 1
                ? photo
                : undefined
            }
            onChange={photos => {
              onItemConfigured({
                photos: photos?.id
                  ? [{ photo: photos, position: 0 }]
                  : photos.map((selectedPhoto, index) => ({
                      photo: selectedPhoto,
                      position: index,
                    })),
              });
            }}
            watermarkSettings={watermarkSettings}
          />
          {activeConfigurableItem?.print_product_configuration && (
            <>
              {!activeConfig ? (
                <PrintProductGroupConfiguratorModal
                  forceOpen
                  printProducts={configurablePrintProductItems.map(
                    printProductItem => ({
                      id: printProductItem?.id,
                      printProductId:
                        printProductItem?.print_product_configuration
                          .print_product.id,
                      printProductName:
                        printProductItem?.print_product_configuration
                          .print_product.name,
                      printProductSizeId:
                        printProductItem?.print_product_configuration
                          .print_product_size.id,
                      printProductSizeName:
                        printProductItem?.print_product_configuration
                          .print_product_size.print_size.name,
                      printProductSizeServiceIds: getPrintSizeServiceIds(
                        printProductItem?.print_product_configuration,
                      ),
                      orientation: getPhotoOrientation(photo),
                      // defaultAccountCard: technically supported, but we're not editing
                      // on this modal as of now. If we do, we'll need to get each config
                      // from the cart Item.
                    }),
                  )}
                  photo={photo}
                  onClose={() => {
                    onConfigurationCancel();
                  }}
                  onComplete={configurations => {
                    onItemsConfigured(configurations);
                  }}
                  buttonText="Continue"
                  albumMembershipGroup={albumMembershipGroup}
                  identity={identity}
                  printBackgrounds={printBackgrounds}
                  watermarkSettings={watermarkSettings}
                  isAllPhotos={isAllPhotos}
                  allowMultiPose={printPackage?.is_multi_pose}
                  allowMultiIdentity={printPackage?.is_multi_identity}
                />
              ) : (
                <>
                  <PrintProductConfiguratorModal
                    forceOpen={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product?.card?.id
                    }
                    printProductId={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product.id
                    }
                    printProductSizeId={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product_size.id
                    }
                    printProductSizeServiceIds={getPrintSizeServiceIds(
                      activeConfigurableItem?.print_product_configuration,
                    )}
                    orientation={getPhotoOrientation(photo)}
                    photo={photo}
                    onClose={() => {
                      onConfigurationCancel();
                    }}
                    onComplete={({ accountCard }) => {
                      onItemConfigured({ accountCard });
                    }}
                    defaultAccountCard={activeConfig?.accountCard}
                    buttonText="Continue"
                    controlsDisabled
                    albumMembershipGroup={albumMembershipGroup}
                    watermarkSettings={watermarkSettings}
                  />
                  <PrintProductSizeRoesConfiguratorModal
                    forceOpen={
                      !activeConfigurableItem?.print_product_configuration
                        ?.print_product?.card?.id
                    }
                    printProduct={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product
                    }
                    printProductSize={
                      activeConfigurableItem?.print_product_configuration
                        ?.print_product_size
                    }
                    photo={photo}
                    identity={purchasedPrintPackage?.identity}
                    onComplete={({ roesConfiguration }) => {
                      onItemConfigured({ roesConfiguration });
                    }}
                    albumMembershipGroup={albumMembershipGroup}
                    isAllPhotos={isAllPhotos}
                    onClose={() => onConfigurationCancel()}
                    watermarkSettings={watermarkSettings}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      {isGreenScreenEnabled && (
        <PrintPackageDigitalDownloadAccountCardModal
          forceOpen={digitalDownloadAccountCardModalOpen}
          accountCards={(cartItem?.configurableItems ?? [])
            .filter(item => !!item?.config?.accountCard?.id)
            .map(({ config }) => config.accountCard)}
          onAccountCardSelected={accountCard => {
            onUpdate(cartItem.id, {
              digitalDownloadAccountCard: accountCard,
            });
            if (onConfigurationComplete) {
              onConfigurationComplete({ purchasedPrintPackage });
            } else {
              setConfigurableItemsListModalProps({
                printPackage,
                remainingConfigurableItems: [],
              });
            }
            setDigitalDownloadAccountCardModalOpen(false);
          }}
        />
      )}

      {!!configurableItemsListModalProps && (
        <PrintPackageConfigurableItemCompleteModal
          forceOpen
          onCheckout={onCheckout}
          onConfigureNextItem={onConfigurationStart}
          {...configurableItemsListModalProps}
          onClose={() => {
            setConfigurableItemsListModalProps(null);
          }}
        />
      )}
    </div>
  );
};

AlbumPrintPackageOption.propTypes = propTypes;

export default AlbumPrintPackageOption;
